import { openDB, promisifyRequest } from '../database/db';
import { dateUtils } from '../utils/dateUtils';
import { auditLogger } from '../audit/auditLogger';

const computeDiff = (before, after) => {
  if (!before) return { initial: after };
  const changes = {};
  const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const k of allKeys) {
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) {
      changes[k] = { from: before[k], to: after[k] };
    }
  }
  return changes;
};

export const recordRepository = {
  create: async (projectId, type, payload) => {
    const db = await openDB();
    const timestamp = dateUtils.now();
    const rootId = 'rec-' + timestamp + '-' + Math.random().toString(36).substr(2, 4);

    const newRecord = {
      id: rootId, rootId, projectId, type,
      revision: 1, status: 'active', previousRevisionId: null,
      timestamp, data: payload
    };

    const auditEntry = {
      id: 'aud-' + timestamp, projectId, action: 'CREATE',
      entityType: type, entityId: rootId, revision: 1,
      timestamp, before: null, after: payload, diff: { initialCreation: true }
    };

    return new Promise((resolve, reject) => {
      const tx = db.transaction(['records', 'audit_logs'], 'readwrite');
      tx.oncomplete = () => resolve(newRecord);
      tx.onerror = () => reject('Transaction Failed');
      tx.objectStore('records').put(newRecord);
      tx.objectStore('audit_logs').put(auditEntry);
    });
  },

  revise: async (projectId, rootId, updatedPayload, reason = 'اصلاح') => {
    const db = await openDB();
    const timestamp = dateUtils.now();

    const readTx = db.transaction('records', 'readonly');
    const records = await promisifyRequest(readTx.objectStore('records').index('projectId').getAll(projectId));
    const activeVersion = records.find(r => r.rootId === rootId && r.status === 'active');

    if (!activeVersion) throw new Error('نسخه فعال یافت نشد.');

    const diff = computeDiff(activeVersion.data, updatedPayload);
    const supersededRecord = { ...activeVersion, status: 'superseded', supersededAt: timestamp };
    const newRevisionRecord = {
      id: 'rec-' + timestamp, rootId, projectId, type: activeVersion.type,
      revision: activeVersion.revision + 1, status: 'active',
      previousRevisionId: activeVersion.id, timestamp, data: updatedPayload
    };

    const auditEntry = {
      id: 'aud-' + timestamp, projectId, action: 'REVISE',
      entityType: activeVersion.type, entityId: rootId,
      revision: newRevisionRecord.revision, timestamp,
      before: activeVersion.data, after: updatedPayload, diff, reason
    };

    return new Promise((resolve, reject) => {
      const tx = db.transaction(['records', 'audit_logs'], 'readwrite');
      tx.oncomplete = () => resolve(newRevisionRecord);
      tx.onerror = () => reject('Revision Failed');
      const store = tx.objectStore('records');
      store.put(supersededRecord);
      store.put(newRevisionRecord);
      tx.objectStore('audit_logs').put(auditEntry);
    });
  },

  getActiveRecords: async (projectId, type = null) => {
    const db = await openDB();
    const tx = db.transaction('records', 'readonly');
    const all = await promisifyRequest(tx.objectStore('records').index('projectId').getAll(projectId));
    let activeOnly = (all || []).filter(r => r.status === 'active');
    if (type) activeOnly = activeOnly.filter(r => r.type === type);
    return activeOnly.sort((a, b) => b.timestamp - a.timestamp);
  },

  getHistoryByRootId: async (projectId, rootId) => {
    const db = await openDB();
    const tx = db.transaction('records', 'readonly');
    const all = await promisifyRequest(tx.objectStore('records').index('projectId').getAll(projectId));
    return (all || []).filter(r => r.rootId === rootId).sort((a, b) => b.revision - a.revision);
  }
};
